import { TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { useCallback, useMemo } from 'react';
import CircularIconButton from 'src/components/design-system/CircularIconButton';
import TextInput from 'src/components/TextInput';
import useBoolean from 'src/hooks/useBoolean';
import useDebounceCallback from 'src/hooks/useDebounceCallback';
import useInvoiceScanManager from 'src/hooks/useInvoiceScanManager';
import { informationIcon } from 'src/utils/icons';
import { GetColor } from './GetColor';
import MatchDocumentTable from './MatchDocumentTable';
import { InputColorMap, TextColorMap } from './PoNumberInput';

export default function DocNumberInput() {
  const {
    value: isDocumentMatchModalOpen,
    setTrue: openDocumentMatchModal,
    setFalse: closeDocumentMatchModal,
  } = useBoolean();

  const {
    docNumber,
    setDocNumber,
    getDocValidations,
    ocrPreviewDocument,
    reviewDocumentValidations,
    reviewDocReqRef,
    initialReviewDocValidation,
    initialLoading,
    setReviewDocumentValidations,
    validationMessages,
    isReviewDocumentLoading,
  } = useInvoiceScanManager();

  const { value: isStartTyping, setTrue: startTyping, setFalse: endTyping } = useBoolean();

  const color = useMemo(
    () => GetColor(validationMessages.documentValidationMessage.type),
    [validationMessages]
  );
  const [handleAsyncDocNumberChange] = useDebounceCallback((attachId: number, value: string) => {
    if (value) {
      getDocValidations(attachId, value);
    } else {
      setReviewDocumentValidations((prev) => ({
        ...prev,
        matchedSuppliers: [],
        matchConfidence: '',
        matchStatus: 'DocNumberNotFound',
        mergeAttachmentReview: null,
        ocrDocNumber: '',
      }));
      reviewDocReqRef.current?.cancel();
    }
  });
  const [debounceEndTyping] = useDebounceCallback(endTyping);
  const handleInputChange = useCallback(
    (event: TextBoxChangeEvent) => {
      const newValue = (event.value as string).trim();
      if (newValue.length > 75) return;

      setDocNumber((prev) => ({ ...prev, value: newValue }));
      startTyping();
      handleAsyncDocNumberChange(ocrPreviewDocument?.attachId, newValue);
      debounceEndTyping();
    },
    [
      setDocNumber,
      startTyping,
      handleAsyncDocNumberChange,
      ocrPreviewDocument?.attachId,
      debounceEndTyping,
    ]
  );

  const isShowMatchDocument = useMemo(() => {
    let isShowMatchDocument = true;
    const docStatus = reviewDocumentValidations.matchStatus;

    if (
      isReviewDocumentLoading ||
      !docNumber.value ||
      docStatus === 'DocNumberNotFound' ||
      docStatus === 'NoMatch'
    ) {
      isShowMatchDocument = false;
    } else {
      isShowMatchDocument = true;
    }

    return isShowMatchDocument;
  }, [docNumber.value, isReviewDocumentLoading, reviewDocumentValidations.matchStatus]);

  const confidencePercentage = useMemo(() => {
    if (initialLoading) return '';
    if (docNumber.value !== (initialReviewDocValidation.ocrDocNumber || '')) return '*Edited';
    return docNumber.matchConfidence ? `*${docNumber.matchConfidence}` : '';
  }, [
    docNumber.matchConfidence,
    docNumber.value,
    initialLoading,
    initialReviewDocValidation.ocrDocNumber,
  ]);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between">
        <p className="text-sm font-medium">Doc Number</p>
        <span className="text-sm text-black font-medium">{confidencePercentage}</span>
      </div>
      <div className="relative">
        <TextInput
          className={`${InputColorMap[color]} pr-8`}
          onChange={handleInputChange}
          value={initialLoading ? '' : (docNumber.value as string)}
        />
        {isShowMatchDocument && (
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 z-50 -m-[2px] cursor-pointer">
            <CircularIconButton
              onClick={openDocumentMatchModal}
              className="focus-visible:ring-2 focus-visible:ring-black focus-visible:border-black"
            >
              {informationIcon}
            </CircularIconButton>
          </div>
        )}

        <MatchDocumentTable
          closeModal={closeDocumentMatchModal}
          isModalOpen={isDocumentMatchModalOpen}
          docNumber={docNumber.value as string}
        />
      </div>
      <span className={`text-sm ${TextColorMap[color]} min-h-[20px]`}>
        {!initialLoading && !isStartTyping && validationMessages.documentValidationMessage.message}
      </span>
    </div>
  );
}
